/**
 * peintreDeLEditeur — ce qu'une écriture de forme fait du PEINTRE de l'éditeur :
 * l'arrête-t-elle, le relance-t-elle, ou le laisse-t-elle comme il est
 * (#1868, défaut de l'éditeur suivi par
 * https://github.com/hector-ai-legal/hector-monorepo/issues/1913).
 *
 * WHY un module pur : l'état « peintre arrêté » vit avec le document monté
 * (`CanvasDocumentScope`), pas avec un appel — le gel survit à l'appel qui l'a
 * causé. Ce module ne décide que la TRANSITION, sur ce que l'éditeur a
 * répondu ; il se teste sans éditeur.
 *
 * Règle de prudence : le peintre n'est tenu pour relancé que par un geste
 * MESURÉ comme le relançant (`tests/ws-mocked/mesure-gel-de-peinture.spec.ts`,
 * SuperDoc 2.15.1, 2026-10-04). Tout autre geste le laisse comme il est : un
 * signal de trop coûte une phrase à l'avocat, un signal manqué lui laisse une
 * page fausse sans le dire.
 *
 * Les mesures :
 * - **arrête** : un plan refusé après une étape jouée (D2, D4, D8 à D10, D12,
 *   D13) — la révision a avancé de 2 ou plus ;
 * - **relance** : un plan ACCEPTÉ dont deux étapes au moins ont un effet — de
 *   forme de texte (G21, G24) comme d'alignement (G26, G27). Il fait avancer la
 *   révision du nombre d'étapes avec effet plus un (2 étapes : +3 ; 3 étapes :
 *   +4), alors qu'un plan dont UNE étape a un effet la fait avancer de 1 et ne
 *   relance rien (D2, F1 ; G29 : deux étapes d'alignement dont une sans effet) ;
 * - **relance** : une écriture de paragraphe HORS PLAN, appliquée, de l'une de
 *   ces sortes — chacune mesurée seule sur un montage neuf, page figée au
 *   préalable (G35 à G47 relevés le 2026-10-04, deux passes pour G35 à G42) :
 *   retrait gauche, droit, de première ligne (`setIndentation` : F10, G32,
 *   G33) ; espacement avant, après, interligne (`setSpacing` : F9, G30, G31) ;
 *   côté de bordure posé, annulé (`style:'none'`) ou effacé, bordure entière
 *   effacée (`setBorder`, `clearBorder` : G35, G36, G43 à G45) ; remise à zéro
 *   d'un paragraphe qui porte un alignement, une bordure ou un retrait
 *   (`resetDirectFormatting` : G37 à G39) ; saut de page avant allumé ou éteint
 *   (`setFlowOptions` : G40, G46) ; niveau de plan (`setOutlineLevel` : G41,
 *   G47) ; style nommé (`styles.paragraph.setStyleRef` : G42 ; `setStyle` :
 *   F11). Chacune fait avancer la révision de 1 et laisse une entrée
 *   d'historique ;
 * - **ne change rien** : un plan refusé sans étape jouée (G22), un plan accepté
 *   sans effet (G25), un alignement hors plan (`setAlignment` : F8) — la seule
 *   écriture de paragraphe hors plan mesurée qui ne relance pas, et qu'aucun
 *   verbe n'emploie.
 */

import type { AvanceApresUnRefus } from './repliDeLot';

/** Ce qu'une écriture de forme a fait, vu du peintre. */
export type GesteSurLePeintre =
  /** Un plan que l'éditeur a refusé à l'application : `avance` qualifie la révision relue. */
  | { geste: 'plan_refuse'; avance: AvanceApresUnRefus }
  /** Un plan que l'éditeur a appliqué, sous la révision `avant`, et qui a rendu la révision `apres`. */
  | { geste: 'plan_applique'; avant: string; apres: string }
  /** Une écriture de paragraphe hors plan mesurée comme relançant le peintre ({@link RELANCE_LE_PEINTRE}), que l'éditeur a appliquée. */
  | { geste: 'ecriture_hors_plan_qui_relance' };

/**
 * Le nombre d'étapes d'un plan appliqué qui ont eu un effet, d'après l'avance
 * de la révision : `undefined` quand elle ne se lit pas (révisions non
 * entières, ou avance de 2, que rien de mesuré ne produit).
 */
export function etapesAvecEffet(avant: string, apres: string): number | undefined {
  if (!/^\d+$/.test(avant) || !/^\d+$/.test(apres)) return undefined;
  const avance = Number(apres) - Number(avant);
  if (avance === 0 || avance === 1) return avance;
  return avance >= 3 ? avance - 1 : undefined;
}

/**
 * L'état du peintre après un geste.
 *
 * @param arrete Vrai si le peintre était arrêté avant le geste.
 * @returns Vrai s'il est arrêté après.
 */
export function peintreArreteApres(arrete: boolean, geste: GesteSurLePeintre): boolean {
  switch (geste.geste) {
    case 'plan_refuse':
      return arrete || geste.avance === 'etapes_jouees';
    case 'plan_applique':
      return arrete && (etapesAvecEffet(geste.avant, geste.apres) ?? 0) < 2;
    case 'ecriture_hors_plan_qui_relance':
      return false;
  }
}

/**
 * Ce que chaque écriture de paragraphe HORS PLAN des verbes de forme,
 * appliquée, fait d'un peintre arrêté : `true`, elle le relance — sur la foi
 * de la mesure dite dans l'en-tête, famille par famille. Une écriture nouvelle
 * entre ici à `false` tant qu'elle n'est pas mesurée (règle de prudence).
 * C'est le troisième argument de `ecrireUnParagraphe` (`ecritureDeForme.ts`).
 */
export const RELANCE_LE_PEINTRE: Readonly<Record<'retraits' | 'espacement' | 'bordure' | 'remiseAZero' | 'sautDePageAvant' | 'niveauDePlan' | 'styleNomme' | 'fond' | 'fondDeCellule' | 'cadreDeCase', boolean>> = {
  retraits: true,
  espacement: true,
  bordure: true,
  remiseAZero: true,
  sautDePageAvant: true,
  niveauDePlan: true,
  styleNomme: true,
  // Fond de paragraphe (`setShading`, `clearShading`) et de cellule (`tables.setShading`, `clearShading`), 2026-10-11 :
  // non mesurés sur une page figée.
  fond: false,
  fondDeCellule: false,
  // Cadre d'une case de tableau (`tables.setBorders`), 2026-10-12 : non mesuré sur une page figée.
  cadreDeCase: false,
};

/** L'état du peintre d'un document monté : lu et tenu à jour par toute écriture de forme. */
export interface PeintreDuDocument {
  estArrete(): boolean;
  noter(arrete: boolean): void;
}

/** Un état du peintre qui ne vit que le temps d'un appel : le repli d'un contexte qui n'en porte pas. */
export function peintreLocal(): PeintreDuDocument {
  let arrete = false;
  return { estArrete: () => arrete, noter: (etat) => { arrete = etat; } };
}
